import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import { getSystemNoticeCategoryBadgeClass, getSystemNoticeCategoryLabel } from '../lib/systemNoticeCategory'
import SanitizedRichText from '../ui/SanitizedRichText'
import { useToast } from '../ui/ToastProvider'
import Loading from '../ui/Loading'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'

export default function SystemNoticeDetailPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { show } = useToast()
    const { hasAnyRole } = useAuth()
    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
    const [deleting, setDeleting] = useState(false)

    const { data: notice, isLoading, error } = useQuery({
        queryKey: ['system-notices', id],
        queryFn: async () => {
            const res = await api.get(`/system-notices/${id}`)
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        enabled: !!id
    })

    useEffect(() => {
        if (error) {
            show('공지를 불러오는데 실패했습니다', 'error')
        }
    }, [error, show])

    const handleDelete = async () => {
        if (!window.confirm('이 공지를 삭제하시겠습니까?')) return
        setDeleting(true)
        try {
            await api.delete(`/system-notices/${id}`)
            show('삭제되었습니다', 'success')
            navigate('/ops/system-notices')
        } catch (err) {
            const message = err?.response?.data?.message || '삭제에 실패했습니다'
            show(message, 'error')
        } finally {
            setDeleting(false)
        }
    }

    if (isLoading) {
        return <Loading />
    }

    if (!notice) {
        return (
            <div className="p-8 text-center">
                <p className="text-red-600">운영자 공지를 찾을 수 없습니다.</p>
                <button
                    type="button"
                    onClick={() => navigate('/ops/system-notices')}
                    className="mt-4 rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90"
                >
                    목록으로
                </button>
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-4xl space-y-6 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-bold">운영자 공지</h1>
                <div className="flex flex-wrap items-center gap-2">
                    {isOperator && (
                        <>
                            <Link
                                to={`/ops/system-notices/${id}/edit`}
                                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                            >
                                수정
                            </Link>
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={handleDelete}
                                className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                                {deleting ? '삭제 중…' : '삭제'}
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate('/ops/system-notices')}
                        className="text-sm text-slate-600 hover:text-slate-900"
                    >
                        목록으로
                    </button>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6">
                <div className="mb-4">
                    <div className="mb-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${getSystemNoticeCategoryBadgeClass(notice.category)}`}>
                            {getSystemNoticeCategoryLabel(notice.category)}
                        </span>
                    </div>
                    <h2 className="text-xl font-bold text-slate-900">{notice.title}</h2>
                </div>

                {/* 운영자만 태그와 상세 정보 표시 */}
                {isOperator && (
                    <>
                        <div className="mb-4 flex flex-wrap gap-2">
                            {notice.showAsPopup && (
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                                    팝업
                                </span>
                            )}
                            {notice.showInBoard && (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                                    대시보드
                                </span>
                            )}
                            {notice.notifyWeb && (
                                <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                                    WEB 알림
                                </span>
                            )}
                            {notice.notifyApp && (
                                <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700">
                                    APP 알림
                                </span>
                            )}
                        </div>

                        <div className="mb-4 space-y-2 text-sm text-slate-600">
                            <div>
                                <span className="font-medium">우선순위:</span> {notice.priority || 0}
                            </div>
                            {notice.startAt && (
                                <div>
                                    <span className="font-medium">시작일시:</span> {formatMenuInstant(notice, 'startAt')}
                                </div>
                            )}
                            {notice.endAt && (
                                <div>
                                    <span className="font-medium">종료일시:</span> {formatMenuInstant(notice, 'endAt')}
                                </div>
                            )}
                            {notice.createdBy && (
                                <div>
                                    <span className="font-medium">작성자:</span> {notice.createdBy.name || '-'}
                                </div>
                            )}
                            {notice.isRead && notice.readAt && (
                                <div>
                                    <span className="font-medium">읽은 일시:</span> {formatMenuInstant(notice, 'readAt')}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* 모든 사용자에게 작성일 표시 */}
                {notice.createdAt && (
                    <div className="mb-4 text-sm text-slate-600">
                        <span className="font-medium">작성일:</span> {formatMenuInstant(notice, 'createdAt')}
                    </div>
                )}

                <div className={`${isOperator ? 'border-t pt-4' : 'pt-2'}`}>
                    <SanitizedRichText html={notice.content} className="system-notice-html" />
                </div>
            </div>
        </div>
    )
}
